import * as React from 'react';
import {
  Select,
  LinkIcon,
  PageDefaultIcon,
  PageSectionIcon,
  EmailIcon,
  PhoneIcon,
  AttachmentIcon,
  ElementPopoverIcon,
  ElementDialogIcon,
} from '@packages/systems/spring';
import {LINK_TYPES, type Mode} from './LinkTypes';

type ModeOptionConfig = {
  value: Mode;
  label: string;
  icon: React.ReactElement;
};

const modeOptions = {
  [LINK_TYPES.PAGE]: {
    label: 'Page',
    icon: <PageDefaultIcon />,
  },
  [LINK_TYPES.EXTERNAL]: {
    label: 'URL',
    icon: <LinkIcon />,
  },
  [LINK_TYPES.DYNPAGE]: {
    label: 'Collection page',
    icon: <PageDefaultIcon colorVariant="purple" />,
  },
  [LINK_TYPES.SECTION]: {
    label: 'Page section',
    icon: <PageSectionIcon />,
  },
  [LINK_TYPES.EMAIL]: {label: 'Email', icon: <EmailIcon />},
  [LINK_TYPES.TELEPHONE]: {label: 'Phone', icon: <PhoneIcon />},
  [LINK_TYPES.FILE]: {label: 'File', icon: <AttachmentIcon />},
  [LINK_TYPES.POPOVER]: {
    label: 'Popover',
    icon: <ElementPopoverIcon />,
  },
  [LINK_TYPES.MODAL]: {
    label: 'Modal',
    icon: <ElementDialogIcon />,
  },
} satisfies Record<Mode, Omit<ModeOptionConfig, 'value'>>;

type LinkModeSelectProps = {
  value: Mode;
  onChange: (next: Mode) => void;
  includeOverlays?: boolean;
  ['data-automation-id']?: string;
};

export const LinkModeSelect = ({
  value,
  onChange,
  includeOverlays = false,
  'data-automation-id': dataAutomationId,
}: LinkModeSelectProps) => {
  const optionValues: Mode[] = [
    LINK_TYPES.EXTERNAL,
    LINK_TYPES.DYNPAGE,
    LINK_TYPES.PAGE,
    LINK_TYPES.SECTION,
    LINK_TYPES.EMAIL,
    LINK_TYPES.TELEPHONE,
    LINK_TYPES.FILE,
    ...(includeOverlays ? [LINK_TYPES.POPOVER, LINK_TYPES.MODAL] : []),
  ];
  const options: ModeOptionConfig[] = optionValues.map((optionValue) => ({
    value: optionValue,
    ...modeOptions[optionValue],
  }));

  return (
    <Select
      data-automation-id={dataAutomationId}
      aria-label="Link type"
      value={value}
      options={options}
      onChange={(next, {changeType}) => {
        if (changeType === 'changeEnd' && next) {
          onChange(next);
        }
      }}
    />
  );
};
